/**
 * #466 · Las condiciones que guarda la herramienta de tarifas, evaluadas sin ejecutar código.
 *
 * La definición de cada herramienta vive en `tarifa_herramientas` (Mongo) y trae cadenas de
 * JavaScript: la condición de cada alerta (`alertas[].condicion`) y la de visibilidad de cada
 * widget y sección (`visible`). La pantalla las ejecutaba con `new Function`, que es ejecución de
 * código arbitrario con el origen del ERP: la cadena entra por `POST`/`PUT /tarifa-herramientas`,
 * que **no comprueba rol ni valida la expresión**, así que cualquier usuario autenticado podía
 * dejar ahí una condición que corre en el navegador de quien abra el calculador.
 *
 * Aquí la cadena se analiza y se calcula con un intérprete propio de expresiones. Lo que no existe
 * en esta gramática no se puede escribir: no hay asignación, no hay `new`, no hay funciones, no se
 * alcanza ningún nombre global —sólo los que se pasan en el ámbito— y las únicas llamadas posibles
 * son los métodos puros de la tabla de abajo, que despacha este fichero y no el objeto receptor.
 * Una expresión que no encaje **no se ejecuta de ninguna otra forma**: se rechaza y se registra.
 *
 * El perímetro es ese y no más: `formula.tpc` / `formula.tpi` siguen con `new Function` en
 * `tarifa-calculador-v3.vue`, y eso es otra decisión —son 193 herramientas con fórmula guardada y
 * el motor de precios no es esto—. Lo mismo con la vista previa del editor
 * (`tarifa-editor-herramientas.vue`).
 */

/** Longitud y profundidad máximas. Una condición real ronda los 90 caracteres. */
const MAX_LONGITUD = 2000;
const MAX_PROFUNDIDAD = 40;

/**
 * Las claves que nunca se leen, ni con punto ni entre corchetes. La lectura ya es sólo de
 * propiedades propias —`constructor` y `toString` viven en el prototipo y por ahí no salen—, pero
 * un objeto de datos con una clave llamada `constructor` sí la tendría propia, así que la puerta se
 * cierra por nombre además de por procedencia.
 */
const CLAVES_PROHIBIDAS = ['__proto__', 'constructor', 'prototype'];

/** Palabras que son valores, no nombres del ámbito. */
const LITERALES = { true: true, false: false, null: null, undefined: undefined };

/**
 * Los métodos invocables, por tipo de receptor. Son todos puros y los aplica este fichero con
 * `String.prototype` / `Array.prototype`: nunca se busca el método en el objeto, así que
 * `form.algo.call(...)` o un dato con una clave `indexOf` propia no llevan a ninguna parte.
 *
 * `indexOf` está porque lo usan las condiciones de verdad: RITE pregunta
 * `["calor_p",...].indexOf(form.srv)>=0` para saber si la inspección elegida tiene parte de calor,
 * y la versión anterior lo preguntaba sobre la cadena (`(form.srv||'').indexOf('frio')!==0`). El
 * resto son los vecinos obvios de esa pregunta; si hiciera falta uno más se añade aquí y a las
 * pruebas, que es justo el punto de tener la lista.
 */
const METODOS_CADENA = ['indexOf', 'lastIndexOf', 'includes', 'startsWith', 'endsWith',
  'toLowerCase', 'toUpperCase', 'trim', 'charAt', 'slice', 'substring', 'split'];
const METODOS_LISTA = ['indexOf', 'lastIndexOf', 'includes', 'join', 'slice'];

/** Operadores y signos, de los más largos a los más cortos: `===` tiene que ganarle a `==`. */
const SIGNOS = ['===', '!==', '==', '!=', '>=', '<=', '&&', '||', '>', '<',
  '+', '-', '*', '/', '%', '!', '?', ':', '(', ')', '[', ']', '.', ','];

/** Lo que se rechaza nombrándolo, para que el motivo registrado diga qué llevaba la condición. */
const PROHIBIDOS = [
  { s: '=>', m: 'una función flecha' },
  { s: '??', m: 'el operador ??' },
  { s: '?.', m: 'el acceso opcional ?.' },
  { s: '++', m: 'un incremento' },
  { s: '--', m: 'un decremento' },
  { s: '**', m: 'una potencia' },
  { s: '=', m: 'una asignación' },
  { s: '&', m: 'un operador de bits' },
  { s: '|', m: 'un operador de bits' },
  { s: '^', m: 'un operador de bits' },
  { s: '~', m: 'un operador de bits' },
  { s: ';', m: 'más de una sentencia' },
  { s: '{', m: 'un bloque o un objeto' },
  { s: '}', m: 'un bloque o un objeto' },
  { s: '`', m: 'una plantilla de cadena' }
];

/**
 * El error de cualquier expresión que esta gramática no admita.
 *
 * Es un constructor y no una `class extends Error` a propósito: babel transpila las clases para los
 * objetivos de este front (`Chrome >= 49`, `Firefox >= 31`) y al heredar de `Error` el `instanceof`
 * deja de funcionar, que es justo lo que las pruebas afirman.
 */
function ExpresionNoPermitida(motivo) {
  this.name = 'ExpresionNoPermitida';
  this.message = motivo;
}
ExpresionNoPermitida.prototype = Object.create(Error.prototype);
ExpresionNoPermitida.prototype.constructor = ExpresionNoPermitida;

function rechazar(motivo) {
  throw new ExpresionNoPermitida(motivo);
}

/** Cómo se nombra un token en un mensaje de error: su valor si lo tiene, y si no el signo. */
function texto(t) {
  if (!t) return null;
  return t.v !== undefined ? t.v : t.t;
}

// ───────────────────────────── Analizador léxico ─────────────────────────────

const ESPACIOS = [' ', '\t', '\n', '\r'];

function esDigito(c) { return c >= '0' && c <= '9'; }

function esInicioNombre(c) {
  return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_' || c === '$';
}

function esNombre(c) { return esInicioNombre(c) || esDigito(c); }

/** Los prefijos de base que escribe JavaScript, que son los que `isNaN` da por números. */
const BASES = { x: 16, X: 16, b: 2, B: 2, o: 8, O: 8 };

/** Si `c` es una cifra de esa base: `9` no lo es en octal y `g` no lo es en hexadecimal. */
function esCifraDe(c, base) {
  if (c === undefined) return false;
  const v = Number.parseInt(c, 16);
  return !Number.isNaN(v) && v < base;
}

/** El signo más largo que empieza en `i`, o cadena vacía. */
function signoEn(fuente, i) {
  return SIGNOS.find(function (s) { return fuente.startsWith(s, i); }) || '';
}

/**
 * Lo prohibido se busca contra el signo válido más largo, porque varios empiezan igual: `=` sería
 * un `===` a medias y `|` el principio de un `||`. Gana lo prohibido sólo si es más largo.
 */
function prohibidoEn(fuente, i) {
  const largo = signoEn(fuente, i).length;
  return PROHIBIDOS.find(function (p) {
    return fuente.startsWith(p.s, i) && p.s.length > largo;
  });
}

/**
 * Lee un número. Admite **todo lo que el editor de herramientas puede dejar escrito sin comillas**:
 * decide si el valor tecleado es un número con `isNaN` y lo pega tal cual en la condición, así que
 * `.5`, `1.`, `1e3`, `0x10` o `0b101` llegan a la base igual que un `12` —antes eran expresiones
 * válidas para `new Function`—. Rechazarlos aquí no da un error visible: la alerta desaparece y la
 * sección se queda siempre visible, que es peor que un número raro.
 *
 * Fuera quedan `10n` (un BigInt no es un número de esta gramática) y un nombre pegado a las cifras.
 * Un `017` se lee como diecisiete, decimal: `new Function` lo tomaba por octal —quince— y lo que
 * quiso decir quien lo tecleó en una casilla de minutos es diecisiete.
 */
function leerNumero(fuente, desde) {
  let j = desde;
  const base = fuente[desde] === '0' ? BASES[fuente[desde + 1]] : undefined;
  if (base) {
    j = desde + 2;
    while (esCifraDe(fuente[j], base)) j++;
    if (j === desde + 2) rechazar('el número «' + fuente.slice(desde, j) + '» no está permitido');
  } else {
    while (j < fuente.length && esDigito(fuente[j])) j++;
    if (fuente[j] === '.') {
      j++;
      while (j < fuente.length && esDigito(fuente[j])) j++;
    }
    if (fuente[j] === 'e' || fuente[j] === 'E') {
      let k = j + 1;
      if (fuente[k] === '+' || fuente[k] === '-') k++;
      // Un `e` sin cifras detrás no es un exponente: se deja fuera del número y lo rechaza quien
      // mire el nombre que empieza ahí, con su propio motivo.
      if (esDigito(fuente[k])) {
        j = k;
        while (j < fuente.length && esDigito(fuente[j])) j++;
      }
    }
  }
  if (esInicioNombre(fuente[j])) rechazar('el número «' + fuente.slice(desde, j + 1) + '» no está permitido');
  const cifras = fuente.slice(desde, j);
  const valor = Number(cifras);
  if (Number.isNaN(valor)) rechazar('el número «' + cifras + '» no está permitido');
  return { valor: valor, fin: j };
}

/**
 * ¿El `.` que hay en `i` empieza un número? Sólo donde no puede ser acceso a propiedad: en
 * `form.kw>.5` el token anterior es un operador, y en `form.5` es un nombre.
 */
function puntoEsNumero(tokens, fuente, i) {
  if (!esDigito(fuente[i + 1])) return false;
  const previo = tokens[tokens.length - 1];
  return !previo || ['nombre', 'numero', 'cadena', ')', ']'].indexOf(previo.t) < 0;
}

/** Lee una cadena entre comillas. Sólo los escapes que alguien escribe a mano en un mensaje. */
function leerCadena(fuente, desde) {
  const cierre = fuente[desde];
  let valor = '';
  let i = desde + 1;
  while (i < fuente.length) {
    const c = fuente[i];
    if (c === cierre) return { valor: valor, fin: i + 1 };
    if (c === '\\') {
      const sig = fuente[i + 1];
      if (sig === 'n') valor += '\n';
      else if (sig === 't') valor += '\t';
      else if (sig === '\\' || sig === '\'' || sig === '"') valor += sig;
      else rechazar('el escape \\' + sig + ' no está permitido en una cadena');
      i += 2;
      continue;
    }
    valor += c;
    i++;
  }
  return rechazar('la cadena no se cierra');
}

function tokenizar(fuente) {
  const tokens = [];
  let i = 0;
  while (i < fuente.length) {
    const c = fuente[i];
    if (ESPACIOS.indexOf(c) >= 0) { i++; continue; }

    if (c === '\'' || c === '"') {
      const cad = leerCadena(fuente, i);
      tokens.push({ t: 'cadena', v: cad.valor });
      i = cad.fin;
      continue;
    }

    if (esDigito(c) || (c === '.' && puntoEsNumero(tokens, fuente, i))) {
      const num = leerNumero(fuente, i);
      tokens.push({ t: 'numero', v: num.valor });
      i = num.fin;
      continue;
    }

    if (esInicioNombre(c)) {
      let j = i;
      while (j < fuente.length && esNombre(fuente[j])) j++;
      tokens.push({ t: 'nombre', v: fuente.slice(i, j) });
      i = j;
      continue;
    }

    const prohibido = prohibidoEn(fuente, i);
    if (prohibido) rechazar('no se admite ' + prohibido.m + ' («' + prohibido.s + '»)');

    const signo = signoEn(fuente, i);
    if (!signo) rechazar('el carácter «' + c + '» no está permitido');
    tokens.push({ t: signo });
    i += signo.length;
  }
  return tokens;
}

// ───────────────────────────── Analizador sintáctico ─────────────────────────────

/** Los binarios por precedencia, de menos a más. El cortocircuito de `&&`/`||` lo hace `calcular`. */
const BINARIOS = [
  ['||'],
  ['&&'],
  ['===', '!==', '==', '!='],
  ['>=', '<=', '>', '<'],
  ['+', '-'],
  ['*', '/', '%']
];

/**
 * Descenso recursivo que devuelve el árbol. Se separa del cálculo para poder guardarlo en caché:
 * `isVisible` se llama una vez por widget en cada repintado, y analizar la misma cadena cien veces
 * por pulsación no hace falta (la versión con `new Function` recompilaba igual, pero eso no es
 * motivo para repetirlo).
 */
function analizar(fuente, nombresAmbito) {
  if (typeof fuente !== 'string' || !fuente.trim()) rechazar('la condición está vacía');
  if (fuente.length > MAX_LONGITUD) rechazar('la condición pasa de ' + MAX_LONGITUD + ' caracteres');

  const tokens = tokenizar(fuente);
  let pos = 0;
  let hondo = 0;

  function mirar() { return tokens[pos]; }
  function es(tipo) { const t = mirar(); return !!t && t.t === tipo; }

  function tragar(tipo) {
    if (!es(tipo)) {
      rechazar(mirar()
        ? 'se esperaba «' + tipo + '» y hay «' + texto(mirar()) + '»'
        : 'se esperaba «' + tipo + '» y se acabó la condición');
    }
    return tokens[pos++];
  }

  function masHondo(seguir) {
    hondo++;
    if (hondo > MAX_PROFUNDIDAD) rechazar('la condición anida más de ' + MAX_PROFUNDIDAD + ' niveles');
    const nodo = seguir();
    hondo--;
    return nodo;
  }

  function expresion() { return masHondo(ternario); }

  function ternario() {
    const cond = binaria(0);
    if (!es('?')) return cond;
    tragar('?');
    const siSi = expresion();
    tragar(':');
    return { t: 'ter', c: cond, a: siSi, b: expresion() };
  }

  function binaria(nivel) {
    if (nivel >= BINARIOS.length) return masHondo(unaria);
    let izq = binaria(nivel + 1);
    while (mirar() && BINARIOS[nivel].indexOf(mirar().t) >= 0) {
      const op = tokens[pos++].t;
      izq = { t: 'bin', op: op, i: izq, d: binaria(nivel + 1) };
    }
    return izq;
  }

  function unaria() {
    if (es('!') || es('-') || es('+')) {
      const op = tokens[pos++].t;
      return { t: 'un', op: op, e: masHondo(unaria) };
    }
    return postfijo();
  }

  function postfijo() {
    let nodo = primaria();
    for (;;) {
      if (es('.')) {
        tragar('.');
        const nombre = tragar('nombre').v;
        if (CLAVES_PROHIBIDAS.indexOf(nombre) >= 0) rechazar('no se puede leer «' + nombre + '»');
        // La ÚNICA forma de llamar que admite la gramática: un método conocido sobre un receptor.
        if (es('(')) {
          if (METODOS_CADENA.indexOf(nombre) < 0 && METODOS_LISTA.indexOf(nombre) < 0) {
            rechazar('el método «' + nombre + '» no está permitido');
          }
          nodo = { t: 'met', o: nodo, n: nombre, a: argumentos() };
          continue;
        }
        nodo = { t: 'prop', o: nodo, k: { t: 'cadena', v: nombre } };
        continue;
      }
      if (es('[')) {
        tragar('[');
        const clave = expresion();
        tragar(']');
        nodo = { t: 'prop', o: nodo, k: clave };
        continue;
      }
      // Un `(` detrás de cualquier otra cosa sería invocar un valor: ni eso ni `form(...)`.
      if (es('(')) rechazar('no se puede invocar nada aquí');
      return nodo;
    }
  }

  /** Los elementos separados por comas de una llamada o de una lista, hasta el cierre. */
  function lista(cierre) {
    const elems = [];
    if (!es(cierre)) {
      elems.push(expresion());
      while (es(',')) { tragar(','); elems.push(expresion()); }
    }
    tragar(cierre);
    return elems;
  }

  function argumentos() {
    tragar('(');
    return lista(')');
  }

  function primaria() {
    if (es('numero')) return { t: 'numero', v: tokens[pos++].v };
    if (es('cadena')) return { t: 'cadena', v: tokens[pos++].v };
    if (es('(')) {
      tragar('(');
      const e = expresion();
      tragar(')');
      return e;
    }
    if (es('[')) {
      tragar('[');
      return { t: 'lista', e: lista(']') };
    }
    if (es('nombre')) {
      const nombre = tokens[pos++].v;
      if (Object.prototype.hasOwnProperty.call(LITERALES, nombre)) {
        return { t: 'lit', v: LITERALES[nombre] };
      }
      if (nombresAmbito.indexOf(nombre) < 0) {
        rechazar('«' + nombre + '» no es uno de los datos de la condición (' + nombresAmbito.join(', ') + ')');
      }
      return { t: 'raiz', n: nombre };
    }
    return rechazar(mirar()
      ? 'no se esperaba «' + texto(mirar()) + '»'
      : 'la condición se queda a medias');
  }

  const arbol = expresion();
  if (pos < tokens.length) rechazar('sobra «' + texto(mirar()) + '» al final de la condición');
  return arbol;
}

// ───────────────────────────── Cálculo ─────────────────────────────

/**
 * Lee una propiedad: **sólo propiedades propias**. Es lo que hace que `form.constructor` no exista
 * aunque el prototipo lo tenga, y coincide con lo que el formulario significa —un campo que nadie
 * ha tecleado vale `undefined`, igual que antes—, así que `(form.kw||0)` sigue dando 0.
 */
function leerPropiedad(objeto, clave) {
  if (objeto === null || objeto === undefined) {
    rechazar('no se puede leer «' + clave + '» de un dato que no existe');
  }
  const k = String(clave);
  if (CLAVES_PROHIBIDAS.indexOf(k) >= 0) rechazar('no se puede leer «' + k + '»');
  return Object.prototype.hasOwnProperty.call(objeto, k) ? objeto[k] : undefined;
}

function esSimple(v) {
  return v === null || v === undefined
    || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** Despacha el método desde `String.prototype` / `Array.prototype`, nunca desde el receptor. */
function invocarMetodo(receptor, nombre, args) {
  for (let k = 0; k < args.length; k++) {
    if (!esSimple(args[k])) rechazar('«' + nombre + '» sólo admite valores simples como argumento');
  }
  if (typeof receptor === 'string') {
    if (METODOS_CADENA.indexOf(nombre) < 0) rechazar('«' + nombre + '» no está permitido sobre una cadena');
    return String.prototype[nombre].apply(receptor, args);
  }
  if (Array.isArray(receptor)) {
    if (METODOS_LISTA.indexOf(nombre) < 0) rechazar('«' + nombre + '» no está permitido sobre una lista');
    return Array.prototype[nombre].apply(receptor, args);
  }
  return rechazar('«' + nombre + '» sólo se puede usar sobre una cadena o una lista');
}

function aplicarBinario(op, i, d) {
  switch (op) {
    case '===': return i === d;
    case '!==': return i !== d;
    case '==': return i == d; // eslint-disable-line eqeqeq
    case '!=': return i != d; // eslint-disable-line eqeqeq
    case '>=': return i >= d;
    case '<=': return i <= d;
    case '>': return i > d;
    case '<': return i < d;
    case '+': return i + d;
    case '-': return i - d;
    case '*': return i * d;
    case '/': return i / d;
    default: return i % d;
  }
}

function calcular(nodo, ambito) {
  switch (nodo.t) {
    case 'numero':
    case 'cadena':
    case 'lit':
      return nodo.v;
    case 'lista':
      return nodo.e.map(function (e) { return calcular(e, ambito); });
    case 'raiz':
      return ambito[nodo.n];
    case 'prop':
      return leerPropiedad(calcular(nodo.o, ambito), calcular(nodo.k, ambito));
    case 'met':
      return invocarMetodo(calcular(nodo.o, ambito), nodo.n, nodo.a.map(function (a) {
        return calcular(a, ambito);
      }));
    case 'un':
      if (nodo.op === '!') return !calcular(nodo.e, ambito);
      if (nodo.op === '-') return -calcular(nodo.e, ambito);
      return +calcular(nodo.e, ambito);
    case 'ter':
      return calcular(nodo.c, ambito) ? calcular(nodo.a, ambito) : calcular(nodo.b, ambito);
    case 'bin':
      // `&&` y `||` cortocircuitan como en JavaScript: una condición que empieza por
      // `[...].indexOf(form.srv)>=0 &&` no debe evaluar lo de la derecha si eso ya es falso.
      if (nodo.op === '&&') return calcular(nodo.i, ambito) && calcular(nodo.d, ambito);
      if (nodo.op === '||') return calcular(nodo.i, ambito) || calcular(nodo.d, ambito);
      return aplicarBinario(nodo.op, calcular(nodo.i, ambito), calcular(nodo.d, ambito));
    default:
      return rechazar('la condición tiene una parte que no se sabe calcular');
  }
}

// ───────────────────────────── Lo que usa la pantalla ─────────────────────────────

/**
 * Los árboles ya analizados, y también los rechazos: una condición mal escrita se queja **una vez**
 * y no en cada repintado. La caché se vacía entera al llegar al tope en vez de desalojar por uso:
 * son unas decenas de condiciones por herramienta y no merece contabilidad.
 */
const CACHE = {};
const MAX_CACHE = 500;
let enCache = 0;

function avisar(fuente, e) {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn('[tarifas] condición rechazada: ' + ((e && e.message) || e) + ' · ' + fuente);
  }
}

function arbolDe(fuente, nombresAmbito) {
  const clave = nombresAmbito.join('|') + '\u0000' + fuente;
  if (Object.prototype.hasOwnProperty.call(CACHE, clave)) return CACHE[clave];
  if (enCache >= MAX_CACHE) {
    Object.keys(CACHE).forEach(function (k) { delete CACHE[k]; });
    enCache = 0;
  }
  let entrada;
  try {
    entrada = { arbol: analizar(fuente, nombresAmbito) };
  } catch (e) {
    entrada = { error: e };
    avisar(fuente, e);
  }
  CACHE[clave] = entrada;
  enCache++;
  return entrada;
}

/** Evalúa y devuelve el valor. Lanza si la condición no encaja en la gramática o no se puede leer. */
function evaluar(fuente, ambito) {
  const datos = ambito || {};
  // El orden sólo tiene que ser ESTABLE, para que `{form, params}` y `{params, form}` compartan
  // entrada en la caché; el comparador va explícito porque `sort()` a secas ordena por la
  // representación de texto y eso no es un criterio (`javascript:S2871`).
  const nombres = Object.keys(datos).sort(function (a, b) { return a.localeCompare(b); });
  const entrada = arbolDe(fuente, nombres);
  if (entrada.error) throw entrada.error;
  return calcular(entrada.arbol, datos);
}

/**
 * ¿Se cumple la condición? `siNoEvalua` es lo que se responde cuando no se puede saber, y lo decide
 * quien pregunta: una alerta que no evalúa no sale (`false`, como cuando `new Function` lanzaba y el
 * `catch` se la comía) y un widget cuya visibilidad no evalúa se pinta (`true`, igual que antes).
 * Lo que no puede pasar es que una condición rota deje la pantalla sin precio.
 */
function cumple(fuente, ambito, siNoEvalua) {
  try {
    return !!evaluar(fuente, ambito);
  } catch (e) {
    return !!siNoEvalua;
  }
}

const ExpresionCondicion = {
  evaluar: evaluar,
  cumple: cumple,
  analizar: analizar,
  ExpresionNoPermitida: ExpresionNoPermitida,
  METODOS_CADENA: METODOS_CADENA,
  METODOS_LISTA: METODOS_LISTA
};

export default ExpresionCondicion;
export { evaluar, cumple, analizar, ExpresionNoPermitida, METODOS_CADENA, METODOS_LISTA };
